import { useEffect, useState } from 'react';
import { EditIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  approveTransactionRequest,
  declineTransactionRequest,
  getDetailedTransactionRequest,
  updateSettlementAmount,
} from 'api/transactions';
import BeneficiaryDetails from 'modules/approve-transactions/BeneficiaryDetails';
import EditAmountModal from 'modules/approve-transactions-v2/components/EditAmountModal';
import {
  POLICY_EVALUATION_STATUS,
  POLICY_EVALUATION_STATUS_TO_COLOR_MAPPING,
  SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS,
} from 'modules/approve-transactions-v2/constants/constants';
import { showError } from 'utils/notifications';
import ApprovalFlow from 'components/ApprovalFlow/ApprovalFlow';
import SkeletonErrorWrapper from 'components/SkeletonErrorWrapper/SkeletonErrorWrapper';

const TransactionRequestDetailsDrawer = ({
  isOpen,
  onClose,
  smbTransactionRequestId,
  refetchPendingTransactions,
  isSettlementTransaction = false,
}) => {
  const [smbTransactionRequestDetails, setSmbTransactionRequestDetails] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [editCurrency, setEditCurrency] = useState('AED');
  const [isUpdating, setIsUpdating] = useState(false);

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const payload = {
        transaction_type: SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS,
      };

      await approveTransactionRequest(smbTransactionRequestDetails?.transaction_id, payload);
    } catch (err) {
      showError(toast, 'Error approving transaction request', err);
    } finally {
      setIsApproving(false);
      refreshTransactionRequest();
    }
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    try {
      const payload = {
        transaction_type: SMB_TRANSACTION_TYPE_SINGLE_PAYOUT_OPS,
      };

      await declineTransactionRequest(smbTransactionRequestDetails?.transaction_id, payload);
    } catch (err) {
      showError(toast, 'Error declining transaction request', err);
    } finally {
      setIsDeclining(false);
      refreshTransactionRequest();
    }
  };

  const fetchDetailedTransactionRequest = async () => {
    setIsLoading(true);
    try {
      const response = await getDetailedTransactionRequest(smbTransactionRequestId);

      setSmbTransactionRequestDetails(response?.data?.data || {});
    } catch (err) {
      setError(err);
    }

    setIsLoading(false);
  };

  const refreshTransactionRequest = async () => {
    setIsLoading(true);
    try {
      const response = await getDetailedTransactionRequest(smbTransactionRequestId);

      setSmbTransactionRequestDetails(response?.data?.data || {});
    } catch (err) {
      showError(toast, 'Error refreshing transaction request', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransactionDataChange = (data) => {
    setTransactionDetails(data);
  };

  const refreshPendingTransactionsList = async () => {
    const terminatingApprovalStatuses = [POLICY_EVALUATION_STATUS.APPROVED, POLICY_EVALUATION_STATUS.DECLINED];

    try {
      if (terminatingApprovalStatuses.includes(smbTransactionRequestDetails?.policy_evaluation_status)) {
        await refetchPendingTransactions();
      }
    } catch (err) {
      showError(toast, 'Error refreshing pending transactions', err);
    }
  };

  useEffect(() => {
    refreshPendingTransactionsList();
  }, [smbTransactionRequestDetails]);

  useEffect(() => {
    if (smbTransactionRequestId) {
      fetchDetailedTransactionRequest();
    }

    return () => {
      setSmbTransactionRequestDetails(null);
      setTransactionDetails(null);
      setError(null);
    };
  }, [smbTransactionRequestId]);

  const handleUpdateAmount = async () => {
    setIsUpdating(true);
    try {
      await updateSettlementAmount(smbTransactionRequestDetails?.transaction_id, {
        settlement_amount: parseFloat(editAmount),
        currency: editCurrency,
      });

      toast({
        title: 'Amount updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setIsEditModalOpen(false);
      refreshTransactionRequest();
    } catch (err) {
      showError(toast, 'Error updating amount', err);
    } finally {
      setIsUpdating(false);
    }
  };

  const openEditModal = () => {
    setEditAmount(smbTransactionRequestDetails?.source_amount?.toString() || '');
    setEditCurrency(smbTransactionRequestDetails?.source_currency_code || 'AED');
    setIsEditModalOpen(true);
  };

  return (
    <>
      <Drawer isOpen={isOpen} placement='right' onClose={onClose} size='xl'>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Payment details</DrawerHeader>
          <DrawerBody>
            <SkeletonErrorWrapper isLoading={isLoading} error={error} errorTitle='Error loading transaction details'>
              {smbTransactionRequestDetails && (
                <Grid templateColumns='repeat(2, 1fr)' gap={6}>
                  <GridItem>
                    <VStack spacing={4} align='stretch'>
                      <HStack justifyContent='space-between'>
                        <Box>
                          <HStack>
                            <Text fontSize='2xl' fontWeight='bold'>
                              {smbTransactionRequestDetails?.source_currency_code}{' '}
                              {smbTransactionRequestDetails?.source_amount}
                            </Text>
                            {isSettlementTransaction && (
                              <Tooltip label='Edit amount and currency'>
                                <IconButton
                                  icon={<EditIcon />}
                                  size='sm'
                                  variant='ghost'
                                  onClick={openEditModal}
                                  aria-label='Edit amount'
                                />
                              </Tooltip>
                            )}
                          </HStack>
                          <Text fontSize='sm' color='gray.500'>
                            {new Date(smbTransactionRequestDetails?.payout_session_created_at).toLocaleString()}
                          </Text>
                        </Box>
                        <Badge
                          colorScheme={
                            POLICY_EVALUATION_STATUS_TO_COLOR_MAPPING[
                              smbTransactionRequestDetails?.policy_evaluation_status
                            ]
                          }
                          fontSize='md'
                        >
                          {smbTransactionRequestDetails?.policy_evaluation_status}
                        </Badge>
                      </HStack>

                      <Heading fontSize='md'>Transaction Details</Heading>

                      <VStack align='left'>
                        <Text fontSize='sm'>Transaction ID: {smbTransactionRequestDetails?.transaction_id}</Text>

                        <Text fontSize='sm' backgroundColor='gray.100' py={2} borderRadius={6}>
                          Receiving amount: {smbTransactionRequestDetails?.dest_currency_code}{' '}
                          {smbTransactionRequestDetails?.receiving_amount}
                        </Text>

                        <Text fontSize='sm'>
                          Exchange Rate: {smbTransactionRequestDetails?.source_currency_code} 1 ={' '}
                          {smbTransactionRequestDetails?.dest_currency_code}{' '}
                          {smbTransactionRequestDetails?.exchange_rate}
                        </Text>

                        <Text fontSize='sm' backgroundColor='gray.100' py={2} borderRadius={6}>
                          Total fees: {smbTransactionRequestDetails?.source_currency_code}{' '}
                          {smbTransactionRequestDetails?.total_cost}
                        </Text>

                        <Text fontSize='sm'>Recipient Name: {smbTransactionRequestDetails?.beneficiary_name}</Text>

                        <Text fontSize='sm' backgroundColor='gray.100' py={2} borderRadius={6}>
                          Recipient Account Number: {smbTransactionRequestDetails?.beneficiary_account_number}
                        </Text>

                        <Text fontSize='sm'>Reference Note: {transactionDetails?.transaction?.comments || 'N/A'}</Text>
                      </VStack>
                    </VStack>
                  </GridItem>

                  <GridItem>
                    <VStack spacing={4} align='stretch'>
                      {smbTransactionRequestDetails?.current_user_approval_status === 'pending' && (
                        <HStack spacing={4} justifyContent='flex-end'>
                          <Button
                            colorScheme='red'
                            onClick={handleDecline}
                            width='150px'
                            variant='outline'
                            isLoading={isDeclining}
                          >
                            Decline
                          </Button>
                          <Button colorScheme='green' onClick={handleApprove} width='150px' isLoading={isApproving}>
                            Approve
                          </Button>
                        </HStack>
                      )}
                      <ApprovalFlow
                        approvalFlow={smbTransactionRequestDetails?.approval_flow}
                        currentPolicyEvaluationLevel={smbTransactionRequestDetails?.current_policy_evaluation_level}
                      />
                    </VStack>
                  </GridItem>
                </Grid>
              )}
            </SkeletonErrorWrapper>

            {smbTransactionRequestDetails?.quote_version == 'V2' && (
              <>
                <Heading size='md' mb={4} mt={4}>
                  Beneficiary Details
                </Heading>
                <BeneficiaryDetails
                  transactionId={smbTransactionRequestDetails?.transaction_id}
                  onTransactionDataChange={handleTransactionDataChange}
                />
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {isSettlementTransaction && (
        <EditAmountModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          editAmount={editAmount}
          setEditAmount={setEditAmount}
          editCurrency={editCurrency}
          setEditCurrency={setEditCurrency}
          onUpdate={handleUpdateAmount}
          isUpdating={isUpdating}
        />
      )}
    </>
  );
};

export default TransactionRequestDetailsDrawer;
